import type React from 'react';
import { useEffect, useState } from 'react';
import { PartnerApiError, type PartnerApiRow, updatePartner } from '@/api/partners';
import { PartnerForm, type PartnerFormState } from '@/components/partners/partner-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  flattenZodErrors,
  parseMetadataInput,
  partnerFormSchema,
  stringifyMetadata,
} from '@/schemas/partner-form-schema';

interface EditPartnerModalProps {
  open: boolean;
  partner: PartnerApiRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const rowToFormState = (partner: PartnerApiRow): PartnerFormState => ({
  partner_name: partner.partner_name,
  partner_code: partner.partner_code,
  party_role_id: partner.party_role_id,
  party_status_id: partner.status_id,
  metadata: stringifyMetadata(partner.metadata),
});

export const EditPartnerModal: React.FC<EditPartnerModalProps> = ({
  open,
  partner,
  onOpenChange,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState<PartnerFormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [metadataError, setMetadataError] = useState<string | undefined>();
  const [rootError, setRootError] = useState<string | null>(null);

  useEffect(() => {
    if (partner && open) {
      setValues(rowToFormState(partner));
      setFieldErrors({});
      setMetadataError(undefined);
      setRootError(null);
    }
  }, [partner, open]);

  const setField = <K extends keyof PartnerFormState>(key: K, value: PartnerFormState[K]) => {
    setValues((prev) => (prev ? { ...prev, [key]: value } : prev));
    // Clear the touched field's error so corrections give immediate feedback
    // rather than leaving a stale message until the next submit.
    setFieldErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
    if (key === 'metadata') setMetadataError(undefined);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setValues(null);
      setFieldErrors({});
      setMetadataError(undefined);
      setRootError(null);
    }
    onOpenChange(next);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partner || !values) return;
    setFieldErrors({});
    setMetadataError(undefined);
    setRootError(null);

    const metadata = parseMetadataInput(values.metadata);
    if (!metadata.ok) {
      setMetadataError(metadata.error);
      return;
    }

    const parsed = partnerFormSchema.safeParse({
      partner_name: values.partner_name,
      partner_code: values.partner_code,
      party_role_id: values.party_role_id === '' ? undefined : values.party_role_id,
      party_status_id: values.party_status_id,
      metadata: metadata.value,
    });
    if (!parsed.success) {
      setFieldErrors(flattenZodErrors(parsed.error));
      return;
    }

    try {
      setSubmitting(true);
      await updatePartner(partner.party_id, {
        partner_name: parsed.data.partner_name,
        partner_code: parsed.data.partner_code,
        party_role_id: parsed.data.party_role_id,
        party_status_id: parsed.data.party_status_id,
        metadata: parsed.data.metadata,
      });
      onSuccess();
      handleOpenChange(false);
    } catch (err: unknown) {
      if (err instanceof PartnerApiError && err.status === 409) {
        const msg =
          (typeof err.body?.message === 'string' && err.body.message) ||
          `Partner with code '${parsed.data.partner_code}' already exists`;
        setFieldErrors((prev) => ({ ...prev, partner_code: msg }));
      } else {
        setRootError(err instanceof Error ? err.message : 'Failed to update partner');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Edit Partner</DialogTitle>
          <DialogDescription>Update partner details.</DialogDescription>
        </DialogHeader>
        {values && (
          <form onSubmit={onSubmit} className="space-y-4">
            <PartnerForm
              idPrefix="edit"
              values={values}
              onChange={setField}
              fieldErrors={fieldErrors}
              metadataError={metadataError}
              showCodeWarning
            />
            {rootError && <p className="text-sm text-destructive">{rootError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
